import React from 'react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { 
  Bell, 
  User, 
  Settings, 
  LogOut, 
  Home,
  Users,
  FileText,
  Wallet,
  BarChart3,
  ClipboardList,
  Calendar,
  UserCog,
  Shield,
  MessageCircle,
  ShoppingCart,
  Building2,
  Activity,
  Menu,
  Heart
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import LogoLockup from '../shared/LogoLockup';
import AdirePattern from '../shared/AdirePattern';
import { getInitials, getProfilePhotoUrl } from '../../lib/profileUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    role, 
    currentUser, 
    getUnreadAlertsCount 
  } = useApp();
  const { logout } = useAuth();

  const unreadAlerts = currentUser ? getUnreadAlertsCount(currentUser.id) : 0;

  const profilePhotoUrl = getProfilePhotoUrl(currentUser);

  const getNavItems = () => {
    switch (role) {
      case 'subscriber':
        return [
          { label: 'Dashboard', path: '/dashboard', icon: Home },
          { label: 'Dependants', path: '/dependants', icon: Users },
          { label: 'Visits', path: '/visits', icon: FileText },
          { label: 'Alerts', path: '/alerts', icon: Bell },
          { label: 'Messages', path: '/messages', icon: MessageCircle },
          { label: 'Wallet', path: '/wallet', icon: Wallet },
        ];
      case 'care_officer':
        return [
          { label: 'Dashboard', path: '/dashboard', icon: Home },
          { label: 'Schedule', path: '/schedule', icon: Calendar },
          { label: 'Assignments', path: '/assignments', icon: Users },
          { label: 'Visits', path: '/visits', icon: FileText },
          { label: 'Marketplace', path: '/marketplace', icon: ShoppingCart },
        ];
      case 'supervisor':
        return [
          { label: 'Dashboard', path: '/dashboard', icon: Home },
          { label: 'Scheduling', path: '/scheduling', icon: Calendar },
          { label: 'Care Officers', path: '/care-officers', icon: UserCog },
          { label: 'Reviews', path: '/reviews', icon: ClipboardList },
          { label: 'Officers', path: '/officers', icon: UserCog },
          { label: 'Performance', path: '/performance', icon: BarChart3 },
        ];
      case 'customer_care':
        return [
          { label: 'Dashboard', path: '/dashboard', icon: Home },
          { label: 'Messages', path: '/messages', icon: MessageCircle },
          { label: 'Alerts', path: '/alerts', icon: Bell },
        ];
      case 'admin':
        return [
          { label: 'Dashboard', path: '/dashboard', icon: Home },
          { label: 'Users', path: '/admin/users', icon: Shield },
          { label: 'Analytics', path: '/analytics', icon: BarChart3 },
          { label: 'Subscribers', path: '/subscribers', icon: Users },
          { label: 'Dependants', path: '/dependants', icon: Heart },
          { label: 'Visits', path: '/visits', icon: FileText },
          { label: 'Officers', path: '/officers', icon: UserCog },
          { label: 'Supervisors', path: '/supervisors', icon: Shield },
          { label: 'Customer Care', path: '/admin/customer-care-agents', icon: MessageCircle },
          { label: 'Partners', path: '/partners', icon: Building2 },
          { label: 'Emergencies', path: '/emergency-analytics', icon: Activity },
        ];
      case 'dependant':
        return [
          { label: 'Home', path: '/dashboard', icon: Home },
          { label: 'Visits', path: '/visits', icon: FileText },
          { label: 'Messages', path: '/messages', icon: MessageCircle },
        ];
      default:
        return [];
    }
  };

  const navItems = getNavItems();
  const desktopPrimaryNavItems = role === 'admin' ? navItems.slice(0, 7) : navItems;
  const desktopOverflowNavItems = role === 'admin' ? navItems.slice(7) : [];
  const isOverflowActive = desktopOverflowNavItems.some(item => (
    location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
  ));

  return (
    <header className="sticky top-0 z-50 w-full max-w-full overflow-visible border-b border-[#E8E6E3] bg-[#F7F4EE]/95 backdrop-blur" data-testid="header">
      <AdirePattern className="pointer-events-none absolute inset-0 h-full w-full blur-[1px] text-[#9F1D35]" opacity={0.3} />
      <div className="relative mx-auto w-full max-w-7xl px-3 sm:px-4 lg:px-6">
        <div className="flex h-[72px] min-w-0 items-center justify-between gap-3">
          {/* Left - Logo & Nav */}
          <div className="flex min-w-0 flex-1 items-center gap-4 xl:gap-8">
            {/* Logo */}
            <div 
              className="flex min-w-0 shrink cursor-pointer items-center"
              onClick={() => navigate('/')}
            >
              <LogoLockup
                size="md"
                className="min-w-0"
                markClassName="h-9 w-9 sm:h-10 sm:w-10"
                textClassName="hidden text-[30px] sm:inline lg:text-[34px]"
              />
            </div>

            {/* Navigation */}
            <nav className="hidden min-w-0 items-center gap-1 xl:flex">
              {desktopPrimaryNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => cn(
                    "whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors border-b-2 border-transparent underline-offset-[6px]",
                    isActive 
                      ? "text-[#1A1A1A] border-[#9F1D35]" 
                      : "text-[#5A5A5A] hover:text-[#1A1A1A]"
                  )}
                >
                  {item.label}
                </NavLink>
              ))}
              {desktopOverflowNavItems.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "relative overflow-hidden rounded-full border border-[#E9B8C2] bg-[#F5E8EA] px-4 py-2 text-sm font-semibold text-[#9F1D35] shadow-sm transition-colors hover:border-[#9F1D35] hover:bg-[#F0D7DC]",
                        isOverflowActive
                          ? "border-[#9F1D35] bg-[#F0D7DC] text-[#7A1628]"
                          : "text-[#9F1D35]"
                      )}
                    >
                      <AdirePattern className="pointer-events-none absolute inset-0 h-full w-full text-[#9F1D35]" opacity={0.16} />
                      <span className="relative">More</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" collisionPadding={12} className="w-56">
                    {desktopOverflowNavItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <DropdownMenuItem key={item.path} asChild>
                          <NavLink
                            to={item.path}
                            className={({ isActive }) => cn(
                              "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm",
                              isActive ? "bg-[#F5E8EA] text-[#9F1D35]" : "text-[#1A1A1A]"
                            )}
                          >
                            <Icon className="h-4 w-4" />
                            {item.label}
                          </NavLink>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>
          </div>

          {/* Right - Actions */}
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#D8D5D0] bg-[#FAFAF7]/80 text-[#9F1D35] xl:hidden"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" collisionPadding={12} className="relative w-[min(18rem,calc(100vw-1.5rem))] overflow-hidden border-[#9F1D35] bg-[#9F1D35] p-2 text-[#FAFAF7]">
                <AdirePattern className="absolute inset-0 h-full w-full text-[#FAFAF7]" opacity={0.12} />
                <div className="relative space-y-1">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.path} asChild className="focus:bg-[#7A1628] focus:text-[#FAFAF7]">
                        <NavLink
                          to={item.path}
                          className={({ isActive }) => cn(
                            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
                            isActive ? 'bg-[#7A1628] text-[#FAFAF7]' : 'text-[#FAFAF7]/85'
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          {item.label}
                        </NavLink>
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator className="bg-[#FAFAF7]/20" />
                  <DropdownMenuItem asChild className="focus:bg-[#7A1628] focus:text-[#FAFAF7]">
                    <NavLink to="/settings" className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-[#FAFAF7]/85">
                      <Settings className="h-4 w-4" />
                      Settings
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="rounded-md px-3 py-2 text-[#FAFAF7] focus:bg-[#7A1628] focus:text-[#FAFAF7]"
                    onClick={async () => {
                      await logout();
                      navigate('/login');
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Notifications */}
            <button 
              className="relative hidden shrink-0 rounded-full p-2 text-[#5A5A5A] transition-colors hover:bg-[#F5E8EA] hover:text-[#1A1A1A] sm:inline-flex"
              data-testid="notifications-button"
            >
              <Bell className="w-5 h-5" />
              {unreadAlerts > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#A33232] rounded-full" />
              )}
            </button>

            {/* User Avatar */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#9F1D35] text-sm font-medium text-[#FAFAF7] ring-2 ring-[#F5E8EA]"
                  data-testid="user-menu"
                  aria-label="Open account menu"
                >
                  {profilePhotoUrl ? (
                    <img src={profilePhotoUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    getInitials(currentUser)
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" collisionPadding={12} className="w-[min(14rem,calc(100vw-1.5rem))]">
                <div className="px-3 py-3 bg-[#F7F4EE]">
                  <p className="text-sm font-medium text-[#1A1A1A]">
                    {currentUser?.firstName} {currentUser?.lastName}
                  </p>
                  <p className="text-xs text-[#5A5A5A] mt-0.5">{currentUser?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <User className="w-4 h-4 mr-2" />
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Preferences
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-[#A33232]"
                  onClick={async () => {
                    await logout();
                    navigate('/login');
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
